<template>
    <div>
        <div class="b_clear_banner">
            <hero-banner-assets :image="hero.image" />

            <div class="container">
                <back-button v-if="!$device.isDesktop" :items="breadcrumbs" />

                <div class="banner_content">
                    <div class="banner_content_inner w100">
                        <div class="content">
                            <breadcrumbs
                                v-if="$device.isDesktop"
                                :items="breadcrumbs"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="b_event">
            <div class="page_decor page_decor_right">
                <div class="image_decor">
                    <picture v-full-image-path:assets="'decor_event_01.svg'" />
                </div>
            </div>
            <div class="page_decor page_decor_left">
                <div class="image_decor">
                    <picture v-full-image-path:assets="'decor_event_02.svg'" />
                </div>
            </div>

            <div class="container">
                <div class="page_title">
                    <h1>{{ pageTitle }}</h1>
                </div>

                <div class="filters_container">
                    <div class="activities_filter box_shadow search">
                        <div class="filter_wrapper flex justify_between">
                            <div class="filter">
                                <div class="filter_form flex">
                                    <div class="form_row search_input_wrapper">
                                        <label class="form_label">
                                            {{
                                                $t(
                                                    'branchActivities.searchLabel'
                                                )
                                            }}
                                        </label>
                                        <input
                                            v-model="params.search"
                                            class="form_control"
                                            type="text"
                                            :placeholder="
                                                $t(
                                                    'branchActivities.searchPlaceholder'
                                                )
                                            "
                                            @keyup.enter="onSearchClick"
                                        />
                                    </div>
                                    <div class="form_row search_btn_wrapper">
                                        <button
                                            class="btn_filtering"
                                            type="button"
                                            @click.prevent="onSearchClick"
                                        >
                                            <div class="icon">
                                                <picture
                                                    v-full-image-path:assets="
                                                        'icon_search.svg'
                                                    "
                                                />
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="activities_filter box_shadow filters">
                        <div class="filter_wrapper flex justify_between">
                            <div class="filter">
                                <div class="filter_form flex">
                                    <div class="form_row search">
                                        <label class="form_label">
                                            {{
                                                $t(
                                                    'branchActivities.searchBranch'
                                                )
                                            }}
                                        </label>
                                        <v-select
                                            v-model="selectedBranches"
                                            :placeholder="
                                                $t(
                                                    'branchActivities.searchBranchPlaceholder'
                                                )
                                            "
                                            :options="branchOptions"
                                            label="title"
                                            multiple
                                            :dir="dir"
                                            class="form_control select"
                                            @search="onBranchesSearch"
                                        >
                                            <template #no-options>
                                                {{
                                                    $t(
                                                        'branchActivities.noResultsFound'
                                                    )
                                                }}
                                            </template>
                                        </v-select>
                                    </div>
                                    <div class="form_row">
                                        <label class="form_label">
                                            {{
                                                $t('branchActivities.fromDate')
                                            }}
                                        </label>

                                        <datepicker
                                            v-model="selectedFromDate"
                                            :max-date="
                                                selectedToDate
                                                    ? new Date(selectedToDate)
                                                    : null
                                            "
                                            custom-class="datepicker_mini"
                                        />
                                    </div>
                                    <div class="form_row">
                                        <label class="form_label">
                                            {{ $t('branchActivities.toDate') }}
                                        </label>

                                        <datepicker
                                            v-model="selectedToDate"
                                            :min-date="
                                                selectedFromDate
                                                    ? new Date(selectedFromDate)
                                                    : null
                                            "
                                            custom-class="datepicker_mini"
                                        />
                                    </div>
                                    <div class="form_row">
                                        <button
                                            class="btn_filtering"
                                            type="button"
                                            @click.prevent="onSearchClick"
                                        >
                                            {{
                                                $t(
                                                    'branchActivities.searchButton'
                                                )
                                            }}
                                        </button>
                                        <button
                                            class="btn_filtering"
                                            type="button"
                                            @click.prevent="onResetClick"
                                        >
                                            {{
                                                $t(
                                                    'branchActivities.resetButton'
                                                )
                                            }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="news_section">
                    <div
                        v-if="articles.length"
                        class="news_list flex justify_between"
                    >
                        <article-item
                            v-for="article in articles"
                            :key="article.id"
                            :article="article"
                            @on-branch-select="refreshContent"
                        />
                    </div>
                    <div v-else class="flex justify_center">
                        <p>{{ $t('branchActivities.noResultsFound') }}</p>
                    </div>
                </div>

                <pagination
                    v-if="isPaginationVisible"
                    :current-page="params.currentPage"
                    :page-count="pageCount"
                    :is-mobile="$device.isMobile"
                    @next-page="fetchPage('next')"
                    @previous-page="fetchPage('previous')"
                    @load-page="fetchPage"
                />
            </div>
        </div>
    </div>
</template>

<script>
import vSelect from 'vue-select';
import { mapActions, mapGetters } from 'vuex';
import { StatusCodes } from 'http-status-codes';

import { ArticleType } from '@/enums/articles';
import Datepicker from '@/components/Datepicker';
import ArticlesList from '@/components/articles/List';
import BackButton from '@/components/elements/BackButton';
import Pagination from '@/components/articles/Pagination';
import Breadcrumbs from '@/components/elements/Breadcrumbs';
import ArticleItem from '@/components/branch-activities/SingleItem';
import HeroBannerAssets from '@/components/elements/HeroBannerAssets';

export default {
    components: {
        vSelect,
        Datepicker,
        BackButton,
        Pagination,
        ArticleItem,
        Breadcrumbs,
        HeroBannerAssets
    },

    extends: ArticlesList,

    async asyncData({
        query: {
            page: currentPage = 1,
            branches = '',
            fromDate = '',
            toDate = '',
            q = ''
        },
        store: { dispatch },
        error
    }) {
        try {
            const params = {
                currentPage: parseInt(currentPage),
                perPage: 12,
                total: 0,
                search: q,
                fromDate,
                toDate,
                type: ArticleType.BRANCH_ACTIVITIES,
                branches: branches || '',
                dateField: 'branchActivityDate',
                sortBy: ['branchActivityDate:DESC', 'createdAt:DESC']
            };

            const [{ rows: articles, count }, defaultBranchOption] =
                await Promise.all([
                    dispatch('articles/index', params),
                    dispatch('branches/index')
                ]);

            params.total = count;

            const branchSlugsArray = params.branches.split(',');
            const branchSlugsSet = new Set(branchSlugsArray);
            const selectedBranches = defaultBranchOption.filter(branch =>
                branchSlugsSet.has(branch.slug)
            );

            return {
                articles,
                params,
                defaultBranchOption,
                branchOptions: defaultBranchOption,
                selectedBranches,
                selectedFromDate: fromDate,
                selectedToDate: toDate,
                timer: null
            };
        } catch (err) {
            if (err.response?.status !== StatusCodes.INTERNAL_SERVER_ERROR) {
                return error({ statusCode: err.response.status });
            }

            console.error(err);

            return error({ statusCode: StatusCodes.INTERNAL_SERVER_ERROR });
        }
    },

    computed: {
        ...mapGetters(['isRtl']),

        dir() {
            return this.isRtl ? 'rtl' : 'ltr';
        },

        pageTitle() {
            return this.$t('branchActivities.pageTitle');
        },

        headTitle() {
            const key = 'headTitles.branchActivities';

            return this.$te(key) ? this.$t(key) : this.pageTitle;
        },

        breadcrumbs() {
            return [
                {
                    text: this.$t('homepage.home'),
                    to: this.localePath('/')
                },
                {
                    text: this.pageTitle,
                    active: true
                }
            ];
        },

        isPaginationVisible() {
            return this.$device.isMobile
                ? this.params.total > this.params.perPage
                : this.articles.length;
        }
    },

    methods: {
        ...mapActions({
            getBranches: 'branches/index'
        }),

        async onSearchClick() {
            this.params.fromDate = this.selectedFromDate;
            this.params.toDate = this.selectedToDate;
            this.params.currentPage = 1;
            this.params.branches = this.selectedBranches
                .map(({ slug }) => slug)
                .join(',');

            await this.fetchItems();
        },

        async onResetClick() {
            this.selectedFromDate = '';
            this.selectedToDate = '';
            this.selectedBranches = [];
            this.params.search = '';

            await this.onSearchClick();
        },

        onBranchesSearch(search, loading) {
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }

            loading(false);

            if (!search.length) {
                this.branchOptions = this.defaultBranchOption;

                return;
            }

            loading(true);

            this.timer = setTimeout(async () => {
                const rows = await this.getBranches({ search });

                this.branchOptions = rows;

                loading(false);
            }, 500);
        },

        updateUrl() {
            const {
                currentPage: page,
                branches,
                fromDate,
                toDate,
                search
            } = this.params;

            this.$router.push({
                query: { page, branches, fromDate, toDate, q: search }
            });
        },

        async refreshContent() {
            const {
                page: currentPage = 1,
                branches = '',
                fromDate = '',
                toDate = '',
                q = ''
            } = this.$route.query;

            this.params.currentPage = parseInt(currentPage);
            this.params.branches = branches;
            this.params.fromDate = fromDate;
            this.params.toDate = toDate;
            this.params.search = q;

            const branchSlugsArray = this.params.branches.split(',');
            const branchSlugsSet = new Set(branchSlugsArray);
            this.selectedBranches = this.defaultBranchOption.filter(branch =>
                branchSlugsSet.has(branch.slug)
            );

            await this.onSearchClick();
        }
    }
};
</script>
